// SPDX-License-Identifier: GPL-3.0-or-later
// The colour palette of the lamps and the effects (THEME.md section 2, "Colour ruling").
// Every hex here is a sigmashake-design token or a neutral gray. No other hue is allowed.
// A colour is never the only cue: each lamp has a label, and each effect has a text cue.
// The Color objects that this file returns are shared and read-only. Copy one before you change it.

import { Color } from "three";
import type { FeedbackKind } from "../../shared/feedback/index.ts";

/** Token colours (public/ss-tokens.css). `brand` is --ss-brand, `allow` is --ss-decision-allow, and so on. */
export const TOKEN_HEX: Readonly<Record<TokenName, number>> = {
  black: 0x000000,
  white: 0xffffff,
  brand: 0xff0000,
  allow: 0x34c759,
  block: 0xff3b30,
  ask: 0xff9500,
  force: 0x5ac8fa,
};

/** The names of the token colours. */
export type TokenName = "black" | "white" | "brand" | "allow" | "block" | "ask" | "force";

/** The neutral grays of THEME.md. */
export const NEUTRAL_HEX: readonly number[] = [
  0x222222, 0x333333, 0x444444, 0x666666, 0x888888, 0xcccccc,
];

/** Every hex that the palette may use. A test checks that each table below stays inside this set. */
export const PALETTE_HEXES: ReadonlySet<number> = new Set<number>([
  ...Object.values(TOKEN_HEX),
  ...NEUTRAL_HEX,
]);

/** The families of table elements. */
export type ElementFamily =
  | "bumper"
  | "sling"
  | "target"
  | "spinner"
  | "ramp"
  | "orbit"
  | "lane"
  | "kicker";

/** One colour for each element family. */
export const FAMILY_HEX: Readonly<Record<ElementFamily, number>> = {
  bumper: TOKEN_HEX.ask,
  sling: TOKEN_HEX.force,
  target: TOKEN_HEX.block,
  spinner: TOKEN_HEX.block,
  ramp: TOKEN_HEX.allow,
  orbit: TOKEN_HEX.force,
  lane: TOKEN_HEX.ask,
  kicker: TOKEN_HEX.brand,
};

/** One colour for each target bank. The target faces and the bank lamps share it. */
export const BANK_HEX: Readonly<Record<"token" | "deny", number>> = {
  token: TOKEN_HEX.ask,
  deny: TOKEN_HEX.block,
};

/** One colour for each character (a `DECORATION` name). */
export const CHARACTER_HEX: Readonly<Record<string, number>> = {
  Carl: TOKEN_HEX.force,
  Dennis: TOKEN_HEX.block,
  Haley: TOKEN_HEX.white,
  Larry: TOKEN_HEX.allow,
  Prod: TOKEN_HEX.brand,
  Randy: TOKEN_HEX.ask,
  Rex: TOKEN_HEX.brand,
  Tessa: TOKEN_HEX.allow,
};

/** One colour for each mode (a `MODE_ID` value). */
export const MODE_HEX: Readonly<Record<string, number>> = {
  accept_all: TOKEN_HEX.allow,
  works_on_my_machine: TOKEN_HEX.force,
  context_window_full: TOKEN_HEX.ask,
  yolo_friday_deploy: TOKEN_HEX.block,
  agent_swarm: TOKEN_HEX.brand,
  ask_mode_production: TOKEN_HEX.white,
};

/** The mode lamps ModeLamp1 to ModeLamp4 show the four timed modes in this order. */
const MODE_LAMP_ORDER: readonly string[] = [
  "accept_all",
  "works_on_my_machine",
  "context_window_full",
  "yolo_friday_deploy",
];

/** Colour of a mode that has none in the table: white. */
export const DEFAULT_MODE_HEX: number = TOKEN_HEX.white;

/** Colour of an event kind that is not an element family. */
const KIND_HEX: Readonly<Partial<Record<FeedbackKind, number>>> = {
  drop_bank_complete: FAMILY_HEX.target,
  drain: TOKEN_HEX.block,
  jackpot: TOKEN_HEX.allow,
  combo: TOKEN_HEX.force,
  skill_shot: TOKEN_HEX.ask,
  ball_lock: TOKEN_HEX.force,
  multiball_start: TOKEN_HEX.brand,
  tilt_warning: TOKEN_HEX.ask,
  tilt: TOKEN_HEX.block,
  extra_ball: TOKEN_HEX.allow,
  ball_save: TOKEN_HEX.allow,
  mode_complete: TOKEN_HEX.allow,
  high_score: TOKEN_HEX.ask,
  stage_start: TOKEN_HEX.brand,
  stage_clear: TOKEN_HEX.allow,
  perk_pick: TOKEN_HEX.ask,
  run_clear: TOKEN_HEX.brand,
  hurry_up_start: TOKEN_HEX.force,
  hurry_up_collect: TOKEN_HEX.allow,
  frenzy_start: TOKEN_HEX.force,
  combo_chain: TOKEN_HEX.force,
  extra_ball_lit: TOKEN_HEX.allow,
  level_best: TOKEN_HEX.ask,
  boss_start: TOKEN_HEX.brand,
  boss_hit: TOKEN_HEX.block,
  boss_phase: TOKEN_HEX.ask,
  boss_telegraph: TOKEN_HEX.brand,
  boss_shield: TOKEN_HEX.white,
  boss_defeat: TOKEN_HEX.ask,
};

const COLORS = new Map<number, Color>();

/** The value of an own key of a table. A name such as "constructor" gives undefined. */
function own<T>(table: Readonly<Record<string, T>>, key: string | undefined): T | undefined {
  return key !== undefined && Object.hasOwn(table, key) ? table[key] : undefined;
}

/** The shared Color of a palette hex. Do not change it. */
export function colorOf(hex: number): Color {
  let color = COLORS.get(hex);
  if (!color) {
    color = new Color(hex);
    COLORS.set(hex, color);
  }
  return color;
}

/** Colour of an event kind: its element family, or a table moment. Unknown: white. */
export function hexOfKind(kind: FeedbackKind): number {
  if (kind in FAMILY_HEX) return FAMILY_HEX[kind as ElementFamily];
  return KIND_HEX[kind] ?? TOKEN_HEX.white;
}

/** Colour of a character. An unknown name gives white. */
export function characterHex(characterId: string | undefined): number {
  return own(CHARACTER_HEX, characterId) ?? TOKEN_HEX.white;
}

/** Colour of a mode. An unknown id gives `DEFAULT_MODE_HEX`. */
export function modeHex(modeId: string | undefined): number {
  return own(MODE_HEX, modeId) ?? DEFAULT_MODE_HEX;
}

/** The colour of a lamp by its name. The first rule that matches wins. */
const LAMP_RULES: readonly (readonly [RegExp, number])[] = [
  [/^SaucerJackpot/, TOKEN_HEX.allow],
  [/^SaucerWizard/, TOKEN_HEX.brand],
  [/^(SaucerLock|LockLamp)/, TOKEN_HEX.force],
  [/^(Saucer|Kickback)/, FAMILY_HEX.kicker],
  [/^NoGuard/, TOKEN_HEX.block],
  [/^Saver/, TOKEN_HEX.allow],
  [/^Bonus/, TOKEN_HEX.ask],
  [/^ShotLampRamp/, FAMILY_HEX.ramp],
  [/^ShotLampOrbit/, FAMILY_HEX.orbit],
  [/^Spinner/, FAMILY_HEX.spinner],
  [/^Token/, BANK_HEX.token],
  [/^Deny/, BANK_HEX.deny],
  [/^(Lane|Top)/, FAMILY_HEX.lane],
];

/** Colour of a lamp (a light insert) by name. A name that no rule knows keeps `fallback`. */
export function lampHex(name: string, fallback: number): number {
  const mode = /^ModeLamp(\d)$/.exec(name);
  if (mode) {
    const id = MODE_LAMP_ORDER[Number(mode[1]) - 1];
    return id !== undefined ? modeHex(id) : fallback;
  }
  for (const [pattern, hex] of LAMP_RULES) if (pattern.test(name)) return hex;
  return fallback;
}

/** The lamp of a table element, for a hit. Returns null when the element has no lamp. */
export function lampOfElement(element: string | undefined): string | null {
  if (element === undefined) return null;
  const fixed = own(FIXED_LAMPS, element);
  if (fixed !== undefined) return fixed;
  const m = /^(TokenTarget|Deny|Lane|TopLane)(\w+)$/.exec(element);
  if (!m) return null;
  const prefix = own(LAMP_PREFIX, m[1] as string);
  return prefix === undefined ? null : `${prefix}${m[2]}`;
}

const FIXED_LAMPS: Readonly<Record<string, string>> = {
  YoloSpinner: "SpinnerLamp",
  RampLeftExit: "ShotLampRampLeft",
  RampRightExit: "ShotLampRampRight",
  OrbitLeftTop: "ShotLampOrbitLeft",
  OrbitRightTop: "ShotLampOrbitRight",
  Saucer: "SaucerModeLamp",
  KickbackKicker: "KickbackLamp",
};

const LAMP_PREFIX: Readonly<Record<string, string>> = {
  TokenTarget: "TokenLight",
  Deny: "DenyLight",
  Lane: "LaneLight",
  TopLane: "TopLight",
};

/** Bands of the ball trail: from slow to fast. The upper limit is a strength from 0 to 1. */
const SPEED_BANDS: readonly (readonly [number, number])[] = [
  [0.25, TOKEN_HEX.white],
  [0.5, TOKEN_HEX.force],
  [0.8, TOKEN_HEX.ask],
  [1.01, TOKEN_HEX.block],
];

/** Colour of a ball trail for a strength from 0 (slow) to 1 (full speed). */
export function speedHex(strength: number): number {
  for (const [limit, hex] of SPEED_BANDS) if (!(strength >= limit)) return hex;
  return TOKEN_HEX.block;
}

/** Colours of the light show, in turn. */
export const SHOW_HEX: readonly number[] = [
  TOKEN_HEX.brand,
  TOKEN_HEX.ask,
  TOKEN_HEX.allow,
  TOKEN_HEX.force,
];
